import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Image,
  Input,
  Select,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useHistory, useParams } from "react-router-dom";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import ProductApi from "api/product";
import ProductAttributesCard from "components/Product/ProductAttributesCard"; 

const ProductDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProduct, setIsSavingProduct] = useState(false);

  const [product, setProduct] = useState({
    name: "",
    description: "",
    sku: "",
    is_online: false,
    parent_id: "",
    platform: "",
    is_shareable: false,
  });
  const [media, setMedia] = useState({
    image: { url: null, thumb_url: null },
    poster: { url: null, thumb_url: null },
  });
  const [imageFile, setImageFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [posterPreviewUrl, setPosterPreviewUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingPoster, setIsUploadingPoster] = useState(false);
  
  // استیت ویژگی‌ها در اینجا نگهداری می‌شود تا به کامپوننت فرزند پاس داده شود
  const [attributes, setAttributes] = useState([]);

  const imageInputRef = useRef(null);
  const posterInputRef = useRef(null);

  const toNullableNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  useEffect(() => {
    if (!posterFile) {
      setPosterPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(posterFile);
    setPosterPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [posterFile]);

  const fetchProduct = async () => {
    setIsLoading(true);
    try {
      const response = await ProductApi.GetByIdWithCompetitors(id);
      const payload = response?.data?.data || response?.data || {};

      setProduct({
        name: payload.name || "",
        description: payload.description || "",
        sku: payload.sku || "",
        is_online: Boolean(payload.is_online),
        parent_id: payload.parent_id ?? "",
        platform: payload.platform ?? "",
        is_shareable: Boolean(payload.is_shareable),
      });

      setMedia({
        image: {
          url: payload?.image?.url ?? null,
          thumb_url: payload?.image?.thumb_url ?? null,
        },
        poster: {
          url: payload?.poster?.url ?? null,
          thumb_url: payload?.poster?.thumb_url ?? null,
        },
      });

      setAttributes(
        (payload.attributes || []).map((attribute) => ({
          id: attribute.id,
          sku: attribute.sku || "",
          price: attribute.price ?? "",
          stock: attribute.stock ?? "",
          z_type: attribute.z_type ?? "",
          platform: attribute.platform ?? "",
          competitor_attributes: attribute.competitor_attributes || [],
        }))
      );
    } catch (error) {
      toast({ title: "خطا در دریافت اطلاعات محصول", description: error?.response?.data?.message, status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return;
    setIsUploadingImage(true);
    try {
      await ProductApi.UploadImage(id, imageFile);
      toast({ title: "عکس محصول آپلود شد", status: "success", duration: 2500, isClosable: true, position: "top-right" });
      setImageFile(null);
      await fetchProduct();
    } catch (error) {
      toast({ title: "آپلود عکس ناموفق بود", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const uploadPoster = async () => {
    if (!posterFile) return;
    setIsUploadingPoster(true);
    try {
      await ProductApi.UploadPoster(id, posterFile);
      toast({ title: "پوستر آپلود شد", status: "success", duration: 2500, isClosable: true, position: "top-right" });
      setPosterFile(null);
      await fetchProduct();
    } catch (error) {
      toast({ title: "آپلود پوستر ناموفق بود", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsUploadingPoster(false);
    }
  };

  const handleProductField = (key, value) => {
    setProduct((prev) => ({ ...prev, [key]: value }));
  };

  const saveProduct = async () => {
    setIsSavingProduct(true);
    try {
      const payload = {
        name: product.name,
        description: product.description,
        sku: product.sku,
        is_online: Boolean(product.is_online),
        parent_id: toNullableNumber(product.parent_id),
        platform: toNullableNumber(product.platform),
        is_shareable: Boolean(product.is_shareable),
      };
      await ProductApi.Update(id, payload);
      toast({ title: "محصول با موفقیت بروزرسانی شد", status: "success", duration: 2500, isClosable: true, position: "top-right" });
    } catch (error) {
      toast({ title: "بروزرسانی محصول ناموفق بود", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsSavingProduct(false);
    }
  };

  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
        <Text>در حال بارگذاری...</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} gap={6}>
      <Card>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={2}>
            <Button size="sm" leftIcon={<ArrowBackIcon />} onClick={() => history.push("/admin/products")}>
              بازگشت
            </Button>
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              ویرایش محصول #{id}
            </Text>
          </Flex>
          <Badge colorScheme="purple">تعداد ویژگی ها: {attributes.length}</Badge>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: "1fr", lg: "520px 1fr" }} gap={6} alignItems="start" dir="ltr">
            <Box>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Flex alignItems="center" justifyContent="space-between" mb={3}>
                    <Text fontWeight="bold">عکس محصول</Text>
                    <Flex alignItems="center" gap={2}>
                      {media.image.thumb_url ? (
                        <Image src={media.image.thumb_url} alt="image thumb" boxSize="30px" objectFit="cover" borderRadius="md" />
                      ) : (
                        <Text fontSize="sm" color="gray.400">-</Text>
                      )}
                    </Flex>
                  </Flex>

                  <Input ref={imageInputRef} type="file" accept="image/*" display="none" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                  <Box role="button" tabIndex={0} borderWidth="1px" borderRadius="md" overflow="hidden" cursor="pointer" bg="gray.50" _hover={{ borderColor: "teal.400" }} onClick={() => imageInputRef.current?.click()}>
                    {imagePreviewUrl || media.image.url ? (
                      <Image src={imagePreviewUrl || media.image.url} alt="image" w="100%" h="180px" objectFit="contain" />
                    ) : (
                      <Flex h="180px" align="center" justify="center">
                        <Text fontSize="sm" color="gray.500">برای انتخاب عکس کلیک کنید</Text>
                      </Flex>
                    )}
                  </Box>

                  <Flex mt={3} alignItems="center" justifyContent="space-between" gap={3}>
                    <Button size="sm" colorScheme="blue" onClick={uploadImage} isLoading={isUploadingImage} isDisabled={!imageFile}>آپلود</Button>
                  </Flex>
                </Box>

                <Box borderWidth="1px" borderRadius="lg" p={4}>
                  <Flex alignItems="center" justifyContent="space-between" mb={3}>
                    <Text fontWeight="bold">پوستر محصول</Text>
                    <Flex alignItems="center" gap={2}>
                      {media.poster.thumb_url ? (
                        <Image src={media.poster.thumb_url} alt="poster thumb" boxSize="30px" objectFit="cover" borderRadius="md" />
                      ) : (
                        <Text fontSize="sm" color="gray.400">-</Text>
                      )}
                    </Flex>
                  </Flex>

                  <Input ref={posterInputRef} type="file" accept="image/*" display="none" onChange={(e) => setPosterFile(e.target.files?.[0] || null)} />
                  <Box role="button" tabIndex={0} borderWidth="1px" borderRadius="md" overflow="hidden" cursor="pointer" bg="gray.50" _hover={{ borderColor: "teal.400" }} onClick={() => posterInputRef.current?.click()}>
                    {posterPreviewUrl || media.poster.url ? (
                      <Image src={posterPreviewUrl || media.poster.url} alt="poster" w="100%" h="180px" objectFit="contain" />
                    ) : (
                      <Flex h="180px" align="center" justify="center">
                        <Text fontSize="sm" color="gray.500">برای انتخاب پوستر کلیک کنید</Text>
                      </Flex>
                    )}
                  </Box>

                  <Flex mt={3} alignItems="center" justifyContent="space-between" gap={3}>
                    <Button size="sm" colorScheme="blue" onClick={uploadPoster} isLoading={isUploadingPoster} isDisabled={!posterFile}>آپلود</Button>
                  </Flex>
                </Box>
              </Grid>
            </Box>
            
            <Box dir="rtl">
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                <FormControl>
                  <FormLabel>نام محصول</FormLabel>
                  <Input textAlign="left" value={product.name} onChange={(e) => handleProductField("name", e.target.value)} />
                </FormControl>

                <FormControl>
                  <FormLabel>کد محصول (SKU)</FormLabel>
                  <Input textAlign="left" value={product.sku} onChange={(e) => handleProductField("sku", e.target.value)} />
                </FormControl>

                <FormControl>
                  <FormLabel>پلتفرم</FormLabel>
                  <Select textAlign="left" value={product.platform} onChange={(e) => handleProductField("platform", e.target.value)} placeholder="انتخاب پلتفرم">
                    <option value="1">PS4</option>
                    <option value="2">PS5</option>
                    <option value="3">PS4/PS5</option>
                  </Select>
                </FormControl>

                <FormControl gridColumn={{ base: "1", md: "1 / span 2" }}>
                  <FormLabel>توضیحات</FormLabel>
                  <Input textAlign="left" value={product.description} onChange={(e) => handleProductField("description", e.target.value)} />
                </FormControl>

                <Flex alignItems="center" gap={6} gridColumn={{ base: "1", md: "1 / span 2" }}>
                  <Checkbox isChecked={product.is_online} onChange={(e) => handleProductField("is_online", e.target.checked)}>آنلاین</Checkbox>
                  <Checkbox isChecked={product.is_shareable} onChange={(e) => handleProductField("is_shareable", e.target.checked)}>قابل اشتراک</Checkbox>
                </Flex>
                
                <Flex gridColumn={{ base: "1", md: "1 / span 2" }} justifyContent="flex-start">
                  <Button colorScheme="teal" onClick={saveProduct} isLoading={isSavingProduct}>ذخیره محصول</Button>
                </Flex>
              </Grid>
            </Box>
          </Grid>
        </CardBody>
      </Card>

      {/* کامپوننت اختصاصی ویژگی‌ها */}
      <ProductAttributesCard 
        productId={id} 
        attributes={attributes} 
        setAttributes={setAttributes} 
        fetchProduct={fetchProduct} 
      />

    </Flex>
  );
};

export default ProductDetails;